import * as React from 'react';
import { Presenter } from './filter/Presenter';
import { FilterContextProvider } from './FilterContext';
import { IFilter } from './IFilter';

function updateUrlParams(filter: IFilter, newSelectedItemValues: (number | string)[] | null) {
  const queryString = new URLSearchParams(window.location.search);
  if (!newSelectedItemValues || newSelectedItemValues.length === 0) {
    queryString.delete(filter.attribute);
  } else {
    queryString.set(filter.attribute, JSON.stringify(newSelectedItemValues));
  }
  window.history.replaceState({}, '', `${window.location.pathname}?${queryString}`);
}

interface IProps {
  filter: IFilter;
  willUpdateUrl: boolean;
  updateSelectedFilters(attributes: string, values: (number | string)[]): void;
}

/**
 * @deprecated please use a different Filter component if you are not in the External context - these are not Thumbtack styles, and ideally they are banished forever
 */
export function Filter({ filter, willUpdateUrl, updateSelectedFilters: updateSelectedFiltersFromProps }: IProps) {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [selectedItemValues, setSelectedItemValues] = React.useState<(number | string)[]>([]);

  React.useEffect(() => {
    setSelectedItemValues(filter.values);
  }, [filter]);

  const handleFilterClose = React.useCallback(() => setIsOpen(false), []);
  const handleVisibilityToggle = React.useCallback(() => setIsOpen((prev) => !prev), []);

  React.useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
    }
  }, [isOpen]);

  const handleFilterClear = React.useCallback(() => {
    if (willUpdateUrl) updateUrlParams(filter, null);
    updateSelectedFiltersFromProps(filter.attribute, []);
    setIsOpen(false);
  }, [filter, updateSelectedFiltersFromProps, willUpdateUrl]);

  const handleItemSelect = React.useCallback(
    (value: number) => {
      setSelectedItemValues((prev) => {
        const newValues = prev.includes(value) ? prev.filter((arrayItem) => arrayItem !== value) : [...prev, value];
        if (willUpdateUrl) updateUrlParams(filter, newValues);
        updateSelectedFiltersFromProps(filter.attribute, newValues);
        return newValues;
      });
    },
    [updateSelectedFiltersFromProps, filter, willUpdateUrl],
  );

  const handleSearch: React.ChangeEventHandler<HTMLInputElement> = React.useCallback((event) => {
    setSearchValue(event.target.value);
  }, []);

  const { name, items, translations } = filter;

  const value = React.useMemo(
    () => ({
      handleFilterClear,
      handleFilterClose,
      handleItemSelect,
      handleVisibilityToggle,
      handleSearch,
      items,
      searchValue,
      selectedItemValues,
    }),
    [
      handleFilterClear,
      handleFilterClose,
      handleItemSelect,
      handleVisibilityToggle,
      handleSearch,
      items,
      searchValue,
      selectedItemValues,
    ],
  );

  return (
    <FilterContextProvider value={value}>
      <Presenter isOpen={isOpen} name={name} translations={translations} />
    </FilterContextProvider>
  );
}
